import { container, ServiceKeys } from '../../core/service-container';
import { convertTestResultsToGraphResults } from './convert-testresults';
import type { DataManagerInterface, ConfigManagerInterface, StateManagerInterface, AudioEngineInterface, EQProcessorInterface, AppConfig, TestResults, UIState, EQSetting, EnvironmentInterface, EQExporterInterface, EqualLoudnessContour, GraphAdapter, NotificationSystemInterface, UIControllerInterface, LoggerInterface, MathUtilsInterface } from '../../types';
import { Logger } from '../../core/logger';
const logger = Logger.createComponentLogger('AppController');

class AppController {
    components: {
        dataManager?: DataManagerInterface;
        configManager?: ConfigManagerInterface;
        stateManager?: StateManagerInterface;
        audioEngine?: AudioEngineInterface;
        eqProcessor?: EQProcessorInterface;
        graphRenderer?: GraphAdapter | undefined;
        uiController?: UIControllerInterface;
        eventHandlers?: { initializeEventHandlers?: () => void } | undefined;
        notificationSystem?: NotificationSystemInterface;
        modalDialog?: { showConfirmation?: (title: string, message: string, onOk?: () => void, onCancel?: () => void) => void } | undefined;
        eqExporter?: EQExporterInterface | undefined;
        logger?: LoggerInterface;
        environment?: EnvironmentInterface | undefined;
        mathUtils?: MathUtilsInterface;
        appConfig?: AppConfig | undefined;
    };
    isInitialized: boolean;

    constructor() {
        this.components = {};
        this.isInitialized = false;
        
        logger.info('AppController initializing...');
        this.initializeComponents();
    }

    /**
     * Initialize all application components using service container
     */
    initializeComponents(): void {
        try {
            // Get all services from container
            this.components.dataManager = container.get(ServiceKeys.DATA_MANAGER);
            this.components.configManager = container.get(ServiceKeys.CONFIG_MANAGER);
            this.components.stateManager = container.get(ServiceKeys.STATE_MANAGER);
            this.components.audioEngine = container.get(ServiceKeys.AUDIO_ENGINE);
            this.components.eqProcessor = container.get(ServiceKeys.EQ_PROCESSOR);
            // Validate GraphRenderer / GraphAdapter retrieved from the container
            const rawGraphRenderer = container.get(ServiceKeys.GRAPH_RENDERER) as unknown;
            if (rawGraphRenderer && typeof (rawGraphRenderer as any).render === 'function') {
                this.components.graphRenderer = rawGraphRenderer as GraphAdapter;
            } else {
                logger.warn('Registered GraphRenderer does not implement required interface (render). Graph features will be disabled.');
                this.components.graphRenderer = undefined;
            }
            this.components.uiController = container.get(ServiceKeys.UI_CONTROLLER);
            // EventHandlers may not be available yet during initialization - will be set later
            try {
                this.components.eventHandlers = container.get(ServiceKeys.EVENT_HANDLERS);
            } catch (error) {
                logger.warn('EventHandlers not available yet during initialization - will be set later');
                this.components.eventHandlers = undefined;
            }
            this.components.notificationSystem = container.get(ServiceKeys.NOTIFICATION_SYSTEM);
            this.components.modalDialog = container.get(ServiceKeys.MODAL_DIALOG);
            this.components.eqExporter = container.get(ServiceKeys.EQ_EXPORTER);
            this.components.logger = container.get(ServiceKeys.LOGGER);
            this.components.environment = container.get(ServiceKeys.ENVIRONMENT);
            this.components.mathUtils = container.get(ServiceKeys.MATH_UTILS);

            // Initialize app config
            this.components.appConfig = this.components.configManager.getConfig();
            
            // Defer AudioContext creation until a user gesture (Start button).
            // The AudioEngine will be initialized lazily when the user clicks Start
            // (EventHandlers.handleStartStop -> audioEngine.startAudio()) so browsers
            // don't block the context and log autoplay warnings.
            
            // Set up state observers
            this.setupStateObservers();
            
            // Mark as initialized
            this.isInitialized = true;
            logger.info('AppController fully initialized with service container');
            
            // Initialize event handlers if in browser
            if (typeof document !== 'undefined') {
                this.initializeEventHandlers();
            }
        } catch (error) {
            logger.error('AppController initialization failed:', error);
            throw error;
        }
    }
    /**
     * Set up state change observers for synchronization
     */
    setupStateObservers(): void {
        const { stateManager } = this.components;
        
        // Note: Removed global window variable assignments for cleaner architecture
        // The state is now managed through proper dependency injection
        if (typeof window !== 'undefined' && stateManager) {
            // Set up observers for reactive updates
            stateManager.subscribe('contours', () => {
                // Redraw graph when contours change
                this.drawGraph();
            });

            stateManager.subscribe('results', () => {
                // Redraw graph when results change
                this.drawGraph();
            });

            stateManager.subscribe('frequency', () => {
                // Frequency updated - could trigger other UI updates here
            });
        }
    }

    /**
     * Initialize event handlers when DOM is ready
     */
    initializeEventHandlers(): void {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                this.setupEventHandlers();
            });
        } else {
            this.setupEventHandlers();
        }
    }

    /**
     * Set up event handlers using service container
     */
    setupEventHandlers(): void {
        const { stateManager, dataManager, configManager, appConfig, audioEngine, graphRenderer, eqExporter } = this.components;
        
        // Initialize event handlers with proper dependencies
        const eventHandlers = this.components.eventHandlers;
        if (eventHandlers?.initializeEventHandlers) {
            eventHandlers.initializeEventHandlers();
        } else {
            logger.warn('EventHandlers not available yet during initialization - will be set later');
        }
    }

    /**
     * Set EventHandlers reference after it's created (called from main.ts)
     */
    setEventHandlers(eventHandlers: { initializeEventHandlers?: () => void }): void {
        this.components.eventHandlers = eventHandlers;
        logger.info('EventHandlers reference set in AppController');
        
        // Now that EventHandlers is available, initialize it
        if (eventHandlers?.initializeEventHandlers) {
            eventHandlers.initializeEventHandlers();
            logger.info('EventHandlers initialized successfully');
        }
    }

    /**
     * Get a specific component
     * @param componentName - Name of the component to retrieve
     * @returns The requested component
     */
    getComponent(componentName: string): unknown {
        if (!this.isInitialized) {
            logger.warn('AppController not fully initialized yet');
        }
        return (this.components as any)[componentName];
    }

    /**
     * Get all components
     * @returns All components
     */
    getAllComponents(): Record<string, unknown> {
        return { ...this.components };
    }

    /**
     * Check if controller is initialized
     * @returns Initialization status
     */
    isReady(): boolean {
        return this.isInitialized;
    }

    /**
     * Legacy function wrappers for backward compatibility
     */
    
    getTestResults(): TestResults { 
        return this.components.stateManager!.getAllResults(); 
    }
    
    getEqualLoudnessContours(): { [key: string]: EqualLoudnessContour[] } { 
        return this.components.stateManager!.getContours(); 
    }
    
    getCurrentFrequency(): number { 
        return this.components.stateManager!.getCurrentFrequency(); 
    }

    /**
     * Audio control functions
     */
    getIsStarted(): boolean {
        return this.components.audioEngine ? this.components.audioEngine.getIsStarted() : false;
    }

    startAudio(): void {
        if (this.components.audioEngine) {
            this.components.audioEngine.startAudio();
        }
    }

    stopAudio(): void {
        if (this.components.audioEngine) {
            this.components.audioEngine.stopAudio();
        }
    }

    setNoiseVolume(volume: number): void {
        if (this.components.audioEngine) {
            this.components.audioEngine.setNoiseVolume(volume);
        }
    }

    setSineVolume(volume: number): void {
        if (this.components.audioEngine) {
            this.components.audioEngine.setSineVolume(volume);
        }
    }

    setSineFrequency(frequency: number): void {
        if (this.components.audioEngine) {
            this.components.audioEngine.setSineFrequency(frequency);
        }
    }

    /**
     * State management functions
     */
    updateResult(frequency: number, db: number, ear: 'left' | 'right' | 'both' = 'both'): void {
        this.components.stateManager!.updateResult(frequency, db, ear);
    }

    /**
     * Graph rendering function
     */
    drawGraph(): boolean {
        if (!this.components.graphRenderer) {
            logger.warn('GraphRenderer not available');
            return false;
        }

        const rawResults = this.components.stateManager!.getAllResults();
        const results = convertTestResultsToGraphResults(rawResults);
        const selectedEarRaw = (document.getElementById('ear-select') as HTMLSelectElement | null)?.value ?? 'both';
        const selectedEar = (selectedEarRaw === 'left' || selectedEarRaw === 'right' || selectedEarRaw === 'both')
            ? (selectedEarRaw as 'left' | 'right' | 'both')
            : 'both';
        const graphData = {
            results,
            contours: this.components.stateManager!.getContours(),
            selectedEar,
            selectedContour: (document.getElementById('contour-select') as HTMLSelectElement | null)?.value || 'none'
        };
        
        const rendered = this.components.graphRenderer!.render(graphData);
        // Handle both synchronous and asynchronous render() implementations
        if (rendered && typeof (rendered as any).then === 'function') {
            (rendered as Promise<boolean>)
                .then((ok) => {
                    if (!ok) logger.warn('Graph renderer reported failure (async)');
                })
                .catch((err) => {
                    logger.error('Async graph render failed:', err);
                });
            // Optimistically return true for async renders
            return true;
        }

        return Boolean(rendered);
    }

    /**
     * Configuration management
     */
    saveConfiguration(): void {
        const uiState: Partial<UIState> = {
            noiseVolume: Number((document.getElementById('noise-volume') as HTMLInputElement | null)?.value ?? 0),
            contour: (document.getElementById('contour-select') as HTMLSelectElement | null)?.value ?? ''
        };
        this.components.dataManager!.saveConfiguration(
            this.components.stateManager!.getAllResults(), 
            uiState, 
            this.components.appConfig as AppConfig
        );
    }

    loadConfiguration(event: Event, callback?: (data: unknown) => void): void {
        this.components.dataManager!.loadConfiguration(event, (data: unknown) => {
            const payload: any = data as any;
            // Update test results through StateManager
            const newTestResults = payload.testResults || { both: [], left: [], right: [] };
            if (this.components.stateManager && typeof this.components.stateManager.restoreState === 'function') {
                this.components.stateManager.restoreState({ testResults: newTestResults });
            }

            // Update UI state
            if (payload.uiState) {
                if (payload.uiState.noiseVolume !== undefined) {
                    const noiseVolumeSlider = document.getElementById('noise-volume') as HTMLInputElement | null;
                    const noiseVolumeLabel = document.getElementById('noise-volume-label') as HTMLElement | null;
                    if (noiseVolumeSlider) noiseVolumeSlider.value = String(payload.uiState.noiseVolume);
                    if (noiseVolumeLabel) noiseVolumeLabel.textContent = `${payload.uiState.noiseVolume} dB`;
                }
                if (payload.uiState.contour !== undefined) {
                    const contourSelect = document.getElementById('contour-select') as HTMLSelectElement | null;
                    if (contourSelect) contourSelect.value = payload.uiState.contour;
                }
            }

            this.drawGraph();
            if (callback) callback(payload);
        });
    }

    resetResults(): void {
        // Use modal dialog if available
        if (this.components.modalDialog) {
            this.components.modalDialog.showConfirmation(
                'Reset Test Results',
                'Are you sure you want to reset all test results?',
                () => {
                    // Confirmed - reset results
                    this.components.stateManager!.resetResults('all');
                    this.drawGraph();
                },
                () => {
                    // Cancelled - do nothing
                }
            );
        } else if (confirm('Are you sure you want to reset all test results?')) {
            this.components.stateManager!.resetResults('all');
            this.drawGraph();
        }
    }

    calculateEqualization(): void {
        // Use modal dialog if available
        if (this.components.modalDialog) {
            this.components.modalDialog.showConfirmation(
                'Recalculate EQ',
                'Are you sure you want to reset test results and recalculate?',
                () => {
                    // Confirmed - reset and recalculate
                    this.components.stateManager!.resetResults('all');
                    this.drawGraph();
                },
                () => {
                    // Cancelled - do nothing
                }
            );
        } else if (confirm('Are you sure you want to reset test results and recalculate?')) {
            this.components.stateManager!.resetResults('all');
            this.drawGraph();
        }
    }

    /**
     * EQ generation
     */
    generateEqSettings(): EQSetting[] | null {
        const selectedEarRaw = (document.getElementById('ear-select') as HTMLSelectElement | null)?.value ?? 'both';
        const ear = (selectedEarRaw === 'left' || selectedEarRaw === 'right' || selectedEarRaw === 'both')
            ? (selectedEarRaw as 'left' | 'right' | 'both')
            : 'both';
        const contourName = (document.getElementById('contour-select') as HTMLSelectElement | null)?.value || 'corrected';
        
        return this.components.eqProcessor && typeof this.components.eqProcessor.generateEqSettings === 'function'
            ? this.components.eqProcessor.generateEqSettings(
                this.components.stateManager!.getAllResults(), 
                this.components.stateManager!.getContours(), 
                ear,
                contourName,
                // Read fresh config so strength/quality changes made after startup apply
                (this.components.configManager ? this.components.configManager.getConfig() : this.components.appConfig) as AppConfig
            )
            : null;
    }
}

 // ES module export
export { AppController };
export default AppController;